import { useSearchParams } from "react-router-dom";
import { useAuth, useRequireRole } from "../../contexts/AuthContext";
import { useEffect, useMemo, useState } from "react";
import { TimePeriodPicker } from "../../components/TimePeriodPicker/TimePeriodPicker";

import "./FinanceOverview.css";
import ProjectCard from "../../components/ProjectCard/ProjectCard";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ErrorState from "../../components/ErrorState/ErrorState";
import Searchbar from "../../components/Searchbar/Searchbar";
import { useDebounce } from "../../customHooks/useDebounce";
import HeaderComponent from "../../components/HeaderComponent/HeaderComponent";
import PopupComponent from "../../components/PopupComponent/PopupComponent";
import SortSelect, { SortKey } from "../../components/SortSelect/SortSelect";
import { dynamicSort } from "../../utils/dynamicSort";
import { useFinance } from "../../customHooks/useFinance";

function FinanceOverview() {
  const { isFinanceMember, user } = useAuth();
  useRequireRole(isFinanceMember);

  const {
    fetchFinanceOverviews,
    financeOverviews,
    toggleNotifs,
    sendReminder,
    actionloading,
    dataLoading,
    fetchError,
    showConfirmPopup,
    openConfirmPopup,
    closeConfirmPopup,
  } = useFinance();

  const [searchParams] = useSearchParams();

  // filters
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useDebounce(searchInput, 300);

  const [selectedManager, setSelectedManager] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<"all" | "comments" | "checked">("all");
  const [sortBy, setSortBy] = useState<SortKey>("clientName");
  const [thenBy, setThenBy] = useState<SortKey | null>(null);

  const managerOptions = useMemo(() => {
    const managers = financeOverviews.map((o) => o.managerName);
    return [...new Set(managers)].sort((a, b) => a.localeCompare(b));
  }, [financeOverviews]);

  useEffect(() => {
    let managerName = searchParams.get("manager");
    if (!managerName) return;
    if (managerOptions.includes(managerName)) {
      setSelectedManager(managerName);
    }
  }, [searchParams, managerOptions]);

  const filteredOverviews = useMemo(() => {
    const result = dynamicSort([...financeOverviews], sortBy, thenBy);

    const managerExists = financeOverviews.some((o) => o.managerName === selectedManager);

    const term = debouncedSearch.trim().toLowerCase();

    return result.filter((o) => {
      if (selectedManager && managerExists && o.managerName !== selectedManager) return false;
      if (selectedFilter === "comments" && !o.managerComment && !o.supervisorComment) return false;
      if (selectedFilter === "checked" && !(o.checkedByManager && o.checkedBySupervisor))
        return false;
      if (term)
        return (
          o.clientName.toLowerCase().includes(term) ||
          o.projectNumber.toLowerCase().includes(term) ||
          o.projectTitle.toLowerCase().includes(term)
        );
      return true;
    });
  }, [financeOverviews, selectedManager, selectedFilter, debouncedSearch, sortBy, thenBy]);

  if (fetchError && !actionloading) {
    return (
      <div className="finance-page">
        <ErrorState type="fetch" onAction={fetchFinanceOverviews} fullPage />
      </div>
    );
  }

  return (
    <>
      <title>Projektkontrolle - Buchhaltungsübersicht</title>
      <LoadingSpinner loading={actionloading} />
      <div className="finance-page">
        {showConfirmPopup && (
          <PopupComponent
            title="E-Mail Benachrichtigungen senden"
            message="Möchtest du eine E-Mail Benachrichtigung an alle Projektleiter mit offene Projekte senden?"
            confirmAction={sendReminder}
            confirmText="Ja, Projektleiter benachrichtigen."
            closeAction={closeConfirmPopup}
            closeText="Nein, Projektleiter nicht benachrichtigen."
          />
        )}
        <HeaderComponent
          pageTitle="Buchhaltungsübersicht"
          backDestination="/"
          isFinance
          notificationButtonText={`E-Mail Benachrichtigungen ${
            user?.hasNotificationsOn ? "ausschalten" : "einschalten"
          }`}
          onSendReminderToProjectManagers={openConfirmPopup}
          onToggleNotifications={toggleNotifs}
        />

        <div className="content-base finance-content">
          <div className="finance-projects-container">
            <LoadingSpinner loading={dataLoading} isFullscreen={false} />
            {!fetchError &&
              !dataLoading &&
              filteredOverviews.map((overview) => (
                <ProjectCard
                  variant="finance"
                  projectCheck={overview}
                  key={overview.projectNumber}
                />
              ))}
            {!fetchError && !dataLoading && filteredOverviews.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <ErrorState type="empty" />
              </div>
            )}
          </div>

          <div className="finance-filters-container">
            <div className="finance-filters-top">
              <h3 className="filters-title">Filter</h3>
              <label className="finance-filters-label">Zeitraum:</label>
              <TimePeriodPicker variant="finance" />

              <label className="finance-filters-label">Projektfiltern:</label>
              <select
                className="finance-filters-custom"
                value={selectedFilter}
                onChange={(e) =>
                  setSelectedFilter(e.target.value as "all" | "comments" | "checked")
                }
              >
                <option value="all">Alle</option>
                <option value="comments">Kommentare von VG/PL</option>
                <option value="checked">OK zur Verrechnung</option>
              </select>

              <label className="finance-filters-label">Projektleiter:</label>
              <select
                className="finance-filters-custom"
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
              >
                <option key={""} value="">
                  Alle
                </option>
                {managerOptions.map((managerName) => (
                  <option key={managerName} value={managerName}>
                    {managerName}
                  </option>
                ))}
              </select>
              <SortSelect
                sortBy={sortBy}
                setSortBy={setSortBy}
                thenBy={thenBy}
                setThenBy={setThenBy}
                isFinance
              />
            </div>

            <div className="finance-filters-bottom">
              <Searchbar
                setSearchInput={setSearchInput}
                searchInput={searchInput}
                variant="finance"
                entityToSearch="Projekte"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FinanceOverview;
